/**
 * Hala CRM - Admin Groups Management Page
 * System Control: Manage CRM groups
 * Access: Admin ONLY
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import {
  Layers, Search, RefreshCw, Users, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

export default function CRMAdminGroupsPage() {
  const { user, getCRMHeaders } = useOutletContext();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Check access
  const hasAccess = user?.role_id === 'admin';

  const fetchGroups = useCallback(async () => {
    if (!user || !hasAccess) return;
    setLoading(true);
    
    try {
      const res = await axios.get(`${API}/crm/groups`, { headers: getCRMHeaders() });
      setGroups(res.data?.items || res.data || []);
    } catch (e) {
      console.error('Failed to fetch groups:', e);
    } finally {
      setLoading(false);
    }
  }, [user, hasAccess, getCRMHeaders]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Access denied for non-admins
  if (!hasAccess) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: '60px', 
        textAlign: 'center',
        backgroundColor: COLORS.errorLight,
        borderRadius: '12px',
        margin: '40px',
      }}>
        <AlertCircle size={48} color={COLORS.error} style={{ marginBottom: '16px' }} />
        <h2 style={{ color: COLORS.error, margin: '0 0 8px' }}>غير مصرح بالوصول</h2>
        <p style={{ color: COLORS.textMuted, margin: 0 }}>هذه الصفحة متاحة لمدراء النظام فقط</p>
      </div>
    );
  }

  // Filter groups
  const filteredGroups = groups.filter(g => 
    !search || 
    g.name_ar?.includes(search) || 
    g.program?.includes(search)
  );

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
            إدارة المجموعات
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
            إدارة مجموعات العمل ({groups.length} مجموعة)
          </p>
        </div>
        <button
          onClick={fetchGroups}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            backgroundColor: COLORS.white,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          <RefreshCw size={16} />
          تحديث
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={18} color={COLORS.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="بحث بالاسم أو البرنامج..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px 10px 40px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              textAlign: 'right',
            }}
          />
        </div>
      </div>

      {/* Groups Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '20px',
      }}>
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted, gridColumn: '1/-1' }}>
            جاري التحميل...
          </div>
        ) : filteredGroups.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', gridColumn: '1/-1' }}>
            <Layers size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.textMuted} />
            <p style={{ margin: 0, color: COLORS.textMuted }}>لا توجد مجموعات</p>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div
              key={group.group_id}
              style={{
                backgroundColor: COLORS.white,
                borderRadius: '12px',
                border: `1px solid ${COLORS.border}`,
                padding: '20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: `${COLORS.primary}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Layers size={24} color={COLORS.primary} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
                    {group.name_ar || group.name || 'بدون اسم'}
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                    {group.program || 'بدون برنامج'}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: `1px solid ${COLORS.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: COLORS.textMuted, fontSize: '13px' }}>
                  <Users size={16} />
                  <span>{group.members?.length || 0} عضو</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: COLORS.gold, fontSize: '13px' }}>
                  <span>{group.supervisors?.length || 0} مشرف</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
